import _ from 'lodash';

import {
  getRegexpMatches,
  makeVariableFriendly,
} from '../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {ApplicationModel, ApplicationPropertyModel} from '../../models/api/applicationModel';
import {variableRegexp} from '../../services/variableInterpolation/variableInterpolator';

export const appPrefix = 'app';
const appVariableRegex = /^app\.(.+)-(?<appUid>[^:]+):(?<schemaUid>[^:]+):(?<propId>[^:]+)$/g;

export enum ApplicationSchemaStandardPropertiesEnum {
  NAME = 'name',
  DESCRIPTION = 'description',
}

interface MappedSchema {
  name: string;
  uid: string;
  properties: ReadonlyArray<ApplicationPropertyModel>;
}

interface Property {
  name: string;
  id: string | number;
}

/**
 * Computes whether html contains variables from multiple app schemas
 *
 * @param html The string of html
 * @returns A boolean of whether the string contains variables from multiple app schemas
 */
export function computeHtmlHasMultipleAppSchema(html: string) {
  // Find all variables.
  const variableMatches = getRegexpMatches(variableRegexp, html);
  return (
    _(variableMatches)
      .map((variableMatch) => {
        const variableName = variableMatch[1];
        // Find all app variables.
        const appVariableMatches = getRegexpMatches(appVariableRegex, variableName);
        if (appVariableMatches.length === 0) {
          return undefined;
        }
        const appVariableMatch = appVariableMatches[0];
        // Return the unique app schema identifier.
        return `${appVariableMatch.groups?.appUid}:${appVariableMatch.groups?.schemaUid}`;
      })
      .compact()
      .uniq()
      .value().length > 1
  );
}

/**
 * Maps the property to the variable name
 *
 * @param app The installed application model
 * @param schema The mapped application schema object
 * @param property The property object
 * @param isCustom Whether the property is a custom field
 * @returns The variable name of the application property
 */
export function mapPropertyToVariableName(
  app: ApplicationModel,
  schema: MappedSchema,
  property: Property,
  isCustom: boolean,
) {
  return `${appPrefix}.${computeUserFriendlyIdentifier(
    app,
    schema,
    property,
    isCustom,
  )}-${computeUniqueIdentifier(app, schema, property)}`;
}

/**
 * Computes the property's user friendly name
 *
 * @param app The installed application model
 * @param schema The mapped application schema object
 * @param property The property object
 * @param isCustom Whether the property is a custom field
 * @returns The user friendly variable identifier string (that constitutes part of the variable name)
 */
function computeUserFriendlyIdentifier(
  app: ApplicationModel,
  schema: MappedSchema,
  property: Property,
  isCustom: boolean,
) {
  return `${makeVariableFriendly(app.name)}.${makeVariableFriendly(schema.name)}.${
    isCustom ? 'custom.' : ''
  }${makeVariableFriendly(property.name)}`;
}

/**
 * Computes a unique identifier string for an application property
 *
 @param app The installed application model
 * @param schema The mapped application schema object
 * @param property The property object
 * @returns A unique string to identify the application property
 */
function computeUniqueIdentifier(app: ApplicationModel, schema: MappedSchema, property: Property) {
  return `${app.uid}:${schema.uid}:${property.id}`;
}
